/* ===== Tanluxe cart bridge — persist-before-redirect =====
 * Ported from Pause & Connect's proven addToCart() (MM V6) and adapted to this
 * theme's native event system (StandardEvents.cartLinesUpdate from @shopify/events).
 *
 * THE RULE (fixes "empty cart after leaving checkout"): never redirect straight
 * off /cart/add.js. Every add, for BOTH routes, does three things in order:
 *   1) POST /cart/add.js  (with the `sections` for every cart-items-component so
 *      Shopify returns the re-rendered drawer HTML)
 *   2) GET  /cart.js      (read-after-write — reads back the authoritative cart;
 *      this round-trip is what guarantees the line is committed before we move)
 *   3) dispatch the theme-native CartLinesUpdateEvent so the drawer / cart icon
 *      re-render from the returned sections (the drawer's custom content survives
 *      because it lives inside <cart-items-component>, which morphs from sections)
 * ONLY THEN do we open the drawer (1-bottle + one-time) or go to /checkout (2/3).
 *
 * Exposes window.tanluxeAddToCart({ id, quantity, sellingPlan, openDrawer }).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

async function tanluxeAddToCart({ id, quantity = 1, sellingPlan = null, openDrawer = false }) {
  const formData = new FormData();
  formData.append('id', String(id));
  formData.append('quantity', String(quantity));
  if (sellingPlan) formData.append('selling_plan', String(sellingPlan));

  // Ask for the cart sections so the drawer/icon can re-render from server state.
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  // Dispatch the native event BEFORE the fetch so the drawer/cart-items/icon
  // components attach their handlers to this promise; we resolve it once the
  // authoritative cart is read back. (Action 'add' — auto-open is off in this
  // build, so this won't open the drawer on the checkout route.)
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    // 1) add
    const cfg = fetchConfig('javascript', { body: formData });
    const addRes = await fetch(Theme.routes.cart_add_url, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'text/html' },
    });
    const data = await addRes.json();

    if (data.status) {
      // error (e.g. variant unavailable)
      deferred.reject(new Error(data.description || data.message || 'Add to cart failed'));
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    // 2) read-after-write: authoritative cart. This is the persist guarantee —
    //    it forces the add to be fully committed before any navigation.
    const ajaxCart = await (
      await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    ).json();

    // 3) refresh the in-page cart UI from real server state (drawer content +
    //    cart icon). detail.sections drives the cart-items-component morph.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        sections: data.sections,
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        source: 'tanluxe-offer',
        didError: false,
      },
    });

    // Route — only AFTER the cart is committed and the UI refreshed.
    if (openDrawer) {
      // Auto-open is unreliable in this build, so open the theme drawer explicitly.
      const themeDrawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (themeDrawer && typeof themeDrawer.open === 'function') themeDrawer.open();
      else window.location.href = '/cart';
    } else {
      window.location.href = '/checkout';
    }

    return data;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

window.tanluxeAddToCart = tanluxeAddToCart;
